import { api } from './client'

export type FotoDoComprovante = {
  id: number
  nomeOriginal: string
  mime: string
  bytes: number
}

export type EntregaNaLista = {
  id: number

  /** Número do pedido ou da nota, como a expedição escreveu. */
  referencia: string

  cliente: string
  entregueEm: string
  recebidoPor: string | null
  observacoes: string | null
  contatoId: number | null
  registradoPor: string
  registradoEm: string
  atualizadoEm: string | null
  atualizadoPor: string | null
  fotos: FotoDoComprovante[]
}

/**
 * 🔴 `referenciaCurta` não é detalhe de implementação.
 *
 * Busca com referência curta demais devolve **nada**, e a tela precisa dizer
 * isso — senão parece que a entrega não existe, quando o que houve foi um filtro
 * recusado. Quatro dígitos foi o que abriu a conversa de outro cliente em 03/08.
 */
export type BuscaDeEntregas = {
  referenciaCurta: boolean
  minimo: number
  itens: EntregaNaLista[]
}

export type Retencao = {
  dias: number
  padrao: number
  desligado: boolean
}

export type DadosDaEntrega = {
  referencia: string
  cliente: string
  entregueEm: string
  recebidoPor: string
  observacoes: string
}

/**
 * O que a leitura do comprovante — foto ou a primeira página de um PDF — **sugere** para o
 * formulário.
 *
 * 🔴 Todo campo é opcional: o que o modelo não leu — ou o servidor descartou por
 * não passar na conferência — vem `null`, e a pessoa digita. Nada disto foi gravado.
 */
export type TipoDeDocumentoLido =
  | 'canhoto_de_nota_fiscal'
  | 'comprovante_de_transportadora'
  // O "PROTOCOLO DE ENTREGA" da própria gráfica: a referência sugerida é o Nº OS.
  | 'protocolo_de_entrega'
  | 'outro'

export type LeituraDaFoto = {
  tipoDeDocumento: TipoDeDocumentoLido
  referencia: string | null
  cliente: string | null
  recebidoPor: string | null

  /** `AAAA-MM-DD`. A hora não é lida da foto. */
  dataDoRecebimento: string | null
}

const raiz = (marca: string) => `/marcas/${encodeURIComponent(marca)}/entregas`

export const entregas = {
  listar: (marca: string, filtro?: { referencia?: string; contatoId?: number }) => {
    const busca = new URLSearchParams()

    if (filtro?.referencia) busca.set('referencia', filtro.referencia)
    if (filtro?.contatoId) busca.set('contatoId', String(filtro.contatoId))

    const consulta = busca.toString()

    return api.get<BuscaDeEntregas>(`${raiz(marca)}${consulta ? `?${consulta}` : ''}`)
  },

  /** URL da foto — exige sessão e permissão, como o resto do portal. */
  foto: (marca: string, id: number, fotoId: number) =>
    `${import.meta.env.VITE_API_URL ?? ''}${raiz(marca)}/${id}/fotos/${fotoId}`,

  registrar: (marca: string, dados: DadosDaEntrega & { fotos: FileList | File[] }) => {
    const corpo = new FormData()

    corpo.append('referencia', dados.referencia)
    corpo.append('cliente', dados.cliente)

    // 🔴 A data sai daqui **com fuso**. O campo do navegador entrega
    // `2026-08-08T14:30` sem deslocamento nenhum, e o servidor teria de
    // adivinhar — em UTC, isso muda a data em três horas, num campo que decide
    // quando o comprovante será expurgado.
    corpo.append('entregueEm', new Date(dados.entregueEm).toISOString())

    corpo.append('recebidoPor', dados.recebidoPor)
    corpo.append('observacoes', dados.observacoes)

    for (const foto of Array.from(dados.fotos)) corpo.append('fotos', foto)

    return api.enviarFormulario<{ id: number; referencia: string }>(raiz(marca), corpo)
  },

  /**
   * Lê o comprovante (foto ou PDF) e devolve a sugestão. **Não grava nada** — nem o arquivo.
   *
   * A rota e o campo guardam o nome de quando só se lia foto.
   */
  lerFoto: (marca: string, foto: File) => {
    const corpo = new FormData()

    corpo.append('foto', foto)

    return api.enviarFormulario<LeituraDaFoto>(`${raiz(marca)}/leitura-da-foto`, corpo)
  },

  salvar: (marca: string, id: number, dados: DadosDaEntrega & { contatoId: number | null }) =>
    api.put<{ id: number; referencia: string }>(`${raiz(marca)}/${id}`, {
      referencia: dados.referencia,
      cliente: dados.cliente,
      entregueEm: new Date(dados.entregueEm).toISOString(),
      recebidoPor: dados.recebidoPor,
      observacoes: dados.observacoes,
      contatoId: dados.contatoId,
    }),

  excluir: (marca: string, id: number) => api.delete<void>(`${raiz(marca)}/${id}`),

  retencao: () => api.get<Retencao>('/entregas/retencao'),

  salvarRetencao: (dias: number) =>
    api.put<{ dias: number; desligado: boolean }>('/entregas/retencao', { dias }),
}
